import React, { useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { formatMetric } from "../visuals/formatMetric";

/**
 * Option cards side by side, one accentable as recommended.
 *
 * The cards carry the visual summary only. A full feature matrix belongs in a
 * markdown table in the message body — cramming every attribute into cards
 * makes them unscannable, which is the one thing cards are for.
 *
 * Choosing an option sends it as a user turn rather than mutating anything:
 * any write stays behind the host's own confirmation.
 */
const ComparisonGridBlock = observer(({ block }) => {
  const options = (block.options || []).slice(0, 4);
  // Once a card is chosen, lock every card so it cannot be submitted twice.
  // Persist on the block so it stays chosen if the thread re-renders.
  const [chosen, setChosen] = useState(block.chosenPrompt || null);
  if (!options.length) return null;

  const choose = option => {
    if (chosen) return;
    setChosen(option.prompt);
    block.chosenPrompt = option.prompt;
    copilotStore.sendUserMessage(option.prompt);
  };

  return (
    <div className="cpVisual cpVisual--wide">
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <div className={`cpCompare cpCompare--${options.length}`}>
        {options.map((option, index) => (
          <div
            className={`cpCompare__card${option.recommended ? " is-recommended" : ""}`}
            key={option.id || option.title || index}
          >
            {option.recommended && (
              <span className="cpCompare__badge">
                {option.recommendedLabel || "Recommended"}
              </span>
            )}
            <div className="cpCompare__title">{option.title}</div>
            {option.subtitle && (
              <div className="cpCompare__subtitle">{option.subtitle}</div>
            )}
            {option.value != null && (
              <div className="cpCompare__value">
                {formatMetric(option.value, { unit: option.unit })}
              </div>
            )}
            {(option.points || []).length > 0 && (
              <ul className="cpCompare__points">
                {option.points.slice(0, 5).map(point => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
            {option.prompt && (
              <button
                type="button"
                className={`cpCompare__cta${
                  chosen === option.prompt ? " is-chosen" : ""
                }`}
                disabled={copilotStore.streaming || !!chosen}
                onClick={() => choose(option)}
              >
                {chosen === option.prompt ? "Selected" : option.ctaLabel || "Choose"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default ComparisonGridBlock;
